import type { DataSource } from './DataSource'
import type { FieldDef, ReferenceBundle } from '../domain/types'
import type { BookingReceipt, BookingStatus } from '../domain/scheduling'
import type { JourneyScheduleBooking, JourneyScheduleChoices, JourneyScheduleDay, JourneyScheduleOption, JourneyScheduleRequest } from '../domain/journeyScheduling'
import { findJourneySchedule } from '../domain/journeyScheduling'
import { scheduleDate, scheduleInstant } from '../domain/scheduling'
import { env } from '../config/env'
import { http, lid, ticketing } from './http/client'
import { ApiError } from './errors'
import { createSchedulingSource } from './scheduling'

export interface JourneySchedulingSource {
  days(request: JourneyScheduleRequest, fromDate: string, toDate: string): Promise<JourneyScheduleDay[]>
  selectDay(request: JourneyScheduleRequest, date: string): Promise<JourneyScheduleChoices | null>
  options(request: JourneyScheduleRequest, date: string, targetStepId: string, selectedBookings: JourneyScheduleChoices): Promise<JourneyScheduleOption[]>
  book(input: JourneyScheduleBooking): Promise<BookingReceipt>
  status(id: string): Promise<BookingStatus>
  customerFieldValues(fields: FieldDef[], customerId: number): Promise<Record<string, string>>
}
async function availabilityCall<T>(load: () => Promise<T>): Promise<T> {
  try {return await load()} catch (error) {
    if (error instanceof ApiError && (error.status >= 500 || error.status === 0)) {
      throw new ApiError(error.status, error.code, 'Não foi possível consultar a disponibilidade. Tente novamente.')
    }
    throw error
  }
}
const validateBooking = (input: JourneyScheduleBooking) => {
  const options = Object.fromEntries(input.steps.map((step) => [step.clientStepId, input.choices[step.clientStepId] ? [input.choices[step.clientStepId]] : []]))
  if (!findJourneySchedule(input.steps, options, input.scheduledDate, input.choices)) {
    throw new ApiError(400, 'INVALID_JOURNEY_SCHEDULE', 'Escolha horários compatíveis para todos os serviços no dia da jornada.')
  }
}

export function createJourneySchedulingSource(ds: DataSource, referenceData?: ReferenceBundle | null): JourneySchedulingSource {
  const loadReference = () => referenceData ? Promise.resolve(referenceData) : ds.getReferenceData()
  const scheduling = createSchedulingSource(ds)
  if (env.dataSource === 'real') {
    const queuesFor = (reference: Awaited<ReturnType<DataSource['getReferenceData']>>, request: JourneyScheduleRequest) =>
      reference.serviceConfigs.filter((queue) => request.steps.some((step) => queue.serviceIds.includes(step.serviceId) && (!step.serviceConfigId || step.serviceConfigId === queue.serviceConfigId)))
    const dayOptions = async (request: JourneyScheduleRequest, date: string) => {
      const reference = await loadReference()
      const queues = queuesFor(reference, request)
      const [queueSlots, availabilityEntries] = await Promise.all([
        Promise.all(queues.map(async (queue) => ({ queue, slots: await ds.getDaySlots(queue.serviceConfigId, date) }))),
        Promise.all([...new Set(request.steps.map((step) => step.serviceId))]
          .map(async (id) => [id, await scheduling.availability(id, date)] as const)),
      ])
      const availability = new Map(availabilityEntries)
      // Cada sessão/recurso é consultado uma vez nesta operação, mesmo em etapas repetidas.
      const timeRequests = new Map<string, { serviceId: number; sessionId: number; resourceId: number }>()
      for (const step of request.steps) {
        for (const session of availability.get(step.serviceId)!.sessions) {
          if (session.serviceConfigType !== 2 || scheduleInstant(session.end) <= Date.now()) {continue}
          const matchesQueue = queueSlots.some(({ queue, slots }) => queue.serviceIds.includes(step.serviceId) &&
            (!step.serviceConfigId || queue.serviceConfigId === step.serviceConfigId) && slots.some((slot) => slot.sessionId === session.id))
          if (!matchesQueue) {continue}
          const ids = session.resourceIds?.length ? session.resourceIds : session.resourceId ? [session.resourceId] : []
          for (const resourceId of ids.filter((id) => !step.resourceId || step.resourceId === id)) {
            timeRequests.set(`${step.serviceId}:${session.id}:${resourceId}`, { serviceId: step.serviceId, sessionId: session.id, resourceId })
          }
        }
      }
      const times = new Map(await Promise.all([...timeRequests].map(async ([key, item]) =>
        [key, await scheduling.times(item.serviceId, item.sessionId, item.resourceId)] as const)))
      const entries = await Promise.all(request.steps.map(async (step) => {
        const { sessions, resources } = availability.get(step.serviceId)!
        const options = (await Promise.all(sessions.filter((session) => scheduleInstant(session.end) > Date.now() &&
          (session.serviceConfigType === 1 || session.serviceConfigType === 2 || session.hasSlotsLeft)).map(async (session) => {
          const match = queueSlots.find(({ queue, slots }) => queue.serviceIds.includes(step.serviceId) &&
            (!step.serviceConfigId || queue.serviceConfigId === step.serviceConfigId) && slots.some((slot) => slot.sessionId === session.id))
          if (!match) {return []}
          if (session.serviceConfigType !== 2) {
            const ids = session.resourceId ? [session.resourceId] : session.resourceIds ?? []
            return [{ serviceConfigId: match.queue.serviceConfigId, sessionId: session.id, resourceId: session.resourceId ?? undefined,
              resourceName: resources.find((resource) => resource.id === session.resourceId)?.name,
              resources: [...new Set(ids)].map((id) => ({ id, name: resources.find((resource) => resource.id === id)?.name ?? `Atendente ${id}` })),
              start: session.start, end: session.end, bookingMode: 'session' as const }]
          }
          const ids = session.resourceIds?.length ? session.resourceIds : session.resourceId ? [session.resourceId] : []
          return (await Promise.all(ids.filter((id) => !step.resourceId || step.resourceId === id).map(async (resourceId) =>
            (times.get(`${step.serviceId}:${session.id}:${resourceId}`) ?? []).filter((time) => scheduleInstant(time.end) <= scheduleInstant(session.end))
              .map((time) => ({ ...time, resourceId, resourceName: resources.find((resource) => resource.id === resourceId)?.name,
                serviceConfigId: match.queue.serviceConfigId, sessionId: session.id, bookingMode: 'appointment' as const }))))).flat()
        }))).flat()
        return [step.clientStepId, options] as const
      }))
      return Object.fromEntries(entries)
    }
    // Per-service controls often request the same day together. Share only pending
    // requests; a later interaction must still check fresh availability.
    const pendingOptions = new Map<string, ReturnType<typeof dayOptions>>()
    const loadDayOptions = (request: JourneyScheduleRequest, date: string) => {
      const key = JSON.stringify([request, date])
      let pending = pendingOptions.get(key)
      if (!pending) {
        pending = dayOptions(request, date)
        pendingOptions.set(key, pending)
        const clear = () => {pendingOptions.delete(key)}
        void pending.then(clear, clear)
      }
      return pending
    }
    return {
      days: (request, fromDate, toDate) => availabilityCall(async () => {
        const days = (Date.parse(`${toDate}T00:00:00Z`) - Date.parse(`${fromDate}T00:00:00Z`)) / 86_400_000 + 1
        if (!request.steps.length || !Number.isInteger(days) || days < 1 || days > 31) {
          throw new ApiError(400, 'INVALID_AVAILABILITY_REQUEST', 'Escolha os serviços e um intervalo de até 31 dias para consultar.')
        }
        const queues = queuesFor(await loadReference(), request)
        if (!queues.length) {throw new ApiError(400, 'SERVICE_NOT_CONFIGURED', 'Os serviços escolhidos não possuem filas disponíveis nesta unidade.')}
        const result = await http.post<import('./dto/reference.dto').AvailableDaysBatchResponse[]>('v2', ticketing(`locations/${lid()}/serviceConfigs/available-days-batch`), {
          serviceConfigIds: queues.map((queue) => queue.serviceConfigId), startDate: fromDate, days,
        })
        if (!Array.isArray(result)) {throw new ApiError(502, 'INVALID_AVAILABILITY_RESPONSE', 'Não foi possível carregar os dias disponíveis. Tente novamente.')}
        return Array.from({ length: days }, (_, index) => {
          const date = new Date(Date.parse(`${fromDate}T00:00:00Z`) + index * 86_400_000).toISOString().slice(0, 10)
          const availableStepIds = request.steps.filter((step) => result.some((item) =>
            queues.some((queue) => queue.serviceConfigId === item.serviceConfigId && queue.serviceIds.includes(step.serviceId) && (!step.serviceConfigId || queue.serviceConfigId === step.serviceConfigId)) &&
            item.availableDays.some((day) => day.date.slice(0, 10) === date && day.availableCount > 0))).map((step) => step.clientStepId)
          return { date, hasCompleteJourney: availableStepIds.length === request.steps.length, availableStepIds }
        })
      }),
      selectDay: (request, date) => availabilityCall(async () => findJourneySchedule(request.steps, await dayOptions(request, date), date)),
      options: (request, date, targetStepId, choices) => availabilityCall(async () => {
        const options = await loadDayOptions(request, date)
        return (options[targetStepId] ?? []).filter((option) => !!findJourneySchedule(request.steps, options, date, { ...choices, [targetStepId]: option }))
      }),
      book: async (input) => {
        validateBooking(input)
        const result = await http.post<{ aggregatorId: string }>('v2', ticketing(`locations/${lid()}/journeys`), {
          scheduledDate: input.scheduledDate,
          customer: { id: input.customer.id, name: input.customer.nome, email: input.customer.email, phone: input.customer.telefone.replace(/\D/g, ''), document: input.customer.cpf },
          priority: input.priority, templateId: input.templateId, metadata: { ...input.customer.customData, ...input.metadata },
          attendanceType: 'PRESENTIAL', autoConfirm: false,
          steps: input.steps.map((step) => {
            const choice = input.choices[step.clientStepId]
            const { clientStepId: _clientStepId, ...serverStep } = step
            return { required: true, allowSkip: false, ...serverStep, serviceConfigId: choice.serviceConfigId, sessionId: choice.sessionId, resourceId: choice.resourceId, bookedStartTime: choice.bookingMode === 'appointment' ? choice.start : undefined,
              customFields: Object.entries(input.stepFields[step.clientStepId] ?? {}).filter(([key]) => /^customField:\d+$/.test(key)).map(([key, value]) => ({ id: Number(key.slice(12)), value })) }
          }),
        }, { 'Idempotency-Key': input.idempotencyKey })
        if (!result.aggregatorId) {throw new ApiError(502, 'INVALID_BOOKING_RECEIPT', 'Não foi possível obter o protocolo do agendamento.')}
        return { aggregatorId: result.aggregatorId }
      },
      status: scheduling.status.bind(scheduling),
      customerFieldValues: scheduling.customerFields.bind(scheduling),
    }
  }

  const dayCache = new Map<string, Promise<Record<string, JourneyScheduleOption[]>>>()
  const dayOptions = (request: JourneyScheduleRequest, date: string) => {
    const key = JSON.stringify([date, request.steps])
    let result = dayCache.get(key)
    if (!result) {
      result = (async () => {
        const ref = await loadReference()
        const records = await Promise.all(request.steps.map(async (step) => {
          const service = ref.services.find((item) => item.serviceId === step.serviceId)
          const queues = ref.serviceConfigs.filter((queue) => queue.serviceIds.includes(step.serviceId) && (!step.serviceConfigId || queue.serviceConfigId === step.serviceConfigId))
          const options = (await Promise.all(queues.map(async (queue) => {
            const day = new Date(`${date}T12:00:00`)
            if (day.getDay() === 0 || (day.getDate() + queue.serviceConfigId) % 7 === 0) {return []}
            const sessions = await ds.getDaySlots(queue.serviceConfigId, date)
            const duration = (queue.estMin ?? service?.estMin ?? 15) * 60_000
            const list: JourneyScheduleOption[] = []
            for (const session of sessions.filter((item) => item.available)) {
              const start = scheduleInstant(session.start)
              const end = scheduleInstant(session.end)
              for (let cursor = start; cursor + duration <= end; cursor += 45 * 60_000) {
                if (cursor < Date.now()) {continue}
                list.push({ serviceConfigId: queue.serviceConfigId, sessionId: session.sessionId,
                  resourceId: queue.serviceConfigId, resourceName: `Equipe ${queue.nome}`, start: new Date(cursor).toISOString(), end: new Date(cursor + duration).toISOString(), bookingMode: 'appointment' })
              }
            }
            return list
          }))).flat()
          return [step.clientStepId, options] as const
        }))
        return Object.fromEntries(records)
      })()
      dayCache.set(key, result)
      result.catch(() => dayCache.delete(key))
    }
    return result
  }
  return {
    days: async (request, fromDate, toDate) => {
      const days: string[] = []
      const cursor = new Date(`${fromDate}T12:00:00-03:00`)
      while (scheduleDate(cursor) <= toDate && days.length < 31) {days.push(scheduleDate(cursor)); cursor.setUTCDate(cursor.getUTCDate() + 1)}
      return Promise.all(days.map(async (date) => {
        const options = await dayOptions(request, date)
        return { date, hasCompleteJourney: !!findJourneySchedule(request.steps, options, date),
          availableStepIds: request.steps.filter((step) => options[step.clientStepId]?.length).map((step) => step.clientStepId) }
      }))
    },
    selectDay: async (request, date) => findJourneySchedule(request.steps, await dayOptions(request, date), date),
    options: async (request, date, targetStepId, selected) => {
      const options = await dayOptions(request, date)
      return (options[targetStepId] ?? []).filter((option) => !!findJourneySchedule(request.steps, options, date, { ...selected, [targetStepId]: option }))
    },
    book: async (input) => {
      validateBooking(input)
      if (!findJourneySchedule(input.steps, await dayOptions(input, input.scheduledDate), input.scheduledDate, input.choices)) {throw new ApiError(409, 'SLOT_UNAVAILABLE', 'Os horários mudaram. Confira novamente a jornada.')}
      const agg = await ds.createJourney({ cliente: input.customer, templateKey: null, templateId: input.templateId, metadata: input.metadata, priority: input.priority,
        scheduledDate: input.scheduledDate,
        itens: input.steps.map((step) => ({ serviceId: step.serviceId, sequence: step.sequence - 1, serviceConfigId: input.choices[step.clientStepId].serviceConfigId,
          sessionId: input.choices[step.clientStepId].sessionId, resourceId: input.choices[step.clientStepId].resourceId, bookedStartTime: input.choices[step.clientStepId].bookingMode === 'appointment' ? input.choices[step.clientStepId].start : undefined,
          customFields: Object.entries(input.stepFields[step.clientStepId] ?? {}).filter(([key]) => /^customField:\d+$/.test(key)).map(([key, value]) => ({ id: Number(key.slice(12)), value })) })) })
      return { aggregatorId: agg.aggregatorId }
    },
    status: async (id) => {
      const journey = await ds.getJourney(id)
      return journey?.tickets.length
        ? { pending: 0, error: 0, success: journey.tickets.length }
        : { pending: 1, error: 0, success: 0 }
    },
    customerFieldValues: scheduling.customerFields.bind(scheduling),
  }
}
